import React, { useEffect } from "react"
import type { Product, Order } from "../types"

interface PaymentModalProps {
  isOpen: boolean
  onClose: () => void
  product: Product | null
  order: Order | null
  customerEmail: string
  paystackPublicKey: string
  onPaymentSuccess: (reference: string) => void
  onPaymentError: (error: string) => void
  isLoading?: boolean
}

declare global {
  interface Window {
    PaystackPop: {
      setup: (options: any) => {
        openIframe: () => void
      }
    }
  }
}

export function PaymentModal({
  isOpen,
  onClose,
  product,
  order,
  customerEmail,
  paystackPublicKey,
  onPaymentSuccess,
  onPaymentError,
  isLoading = false,
}: PaymentModalProps) {
  const [isProcessing, setIsProcessing] = React.useState(false)
  useEffect(() => {
    if (isOpen && !window.PaystackPop) {
      const script = document.createElement("script")
      script.src = "https://js.paystack.co/v1/inline.js"
      script.async = true
      document.body.appendChild(script)
    }
  }, [isOpen])

  const handlePayment = () => {
    console.log('Payment attempt with key:', paystackPublicKey)
    console.log('Order:', order)
    console.log('Product:', product)
    
    if (!paystackPublicKey) {
      onPaymentError("Paystack public key not configured")
      return
    }
    
    if (!window.PaystackPop || !order || !product) {
      onPaymentError("Payment system not loaded")
      return
    }

    setIsProcessing(true)

    const handler = window.PaystackPop.setup({
      key: paystackPublicKey,
      email: customerEmail,
      amount: order.amount * 100, // Paystack expects amount in kobo
      currency: order.currency,
      ref: order.bookingId ? `booking_${order.bookingId}_${Date.now()}` : `order_${order.orderId}_${Date.now()}`,
      metadata: {
        orderId: order.orderId,
        productName: product.name,
      },
      callback: function(response: any) {
        setIsProcessing(false)
        onPaymentSuccess(response.reference)
        onClose()
      },
      onClose: function() {
        setIsProcessing(false)
        onClose()
      },
    })

    handler.openIframe()
  }

  if (!isOpen || !product || !order) return null

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000000,
        backdropFilter: "blur(4px)",
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "16px",
          padding: "24px",
          maxWidth: "400px",
          width: "90%",
          boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
          position: "relative",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "16px",
            right: "16px",
            background: "none",
            border: "none",
            fontSize: "24px",
            cursor: "pointer",
            color: "#6b7280",
          }}
        >
          ×
        </button>

        <h3
          style={{
            fontSize: "20px",
            fontWeight: "600",
            marginBottom: "16px",
            color: "#1f2937",
          }}
        >
          Complete Payment
        </h3>

        <div style={{ marginBottom: "20px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "16px",
            }}
          >
            {product.imageUrl && (
              <img
                src={product.imageUrl}
                alt={product.name}
                style={{
                  width: "60px",
                  height: "60px",
                  objectFit: "cover",
                  borderRadius: "8px",
                }}
              />
            )}
            <div>
              <h4
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  margin: "0 0 4px 0",
                  color: "#1f2937",
                }}
              >
                {product.name}
              </h4>
              <p
                style={{
                  fontSize: "14px",
                  color: "#6b7280",
                  margin: 0,
                }}
              >
                {product.description}
              </p>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "16px",
              backgroundColor: "#f9fafb",
              borderRadius: "8px",
              marginBottom: "20px",
            }}
          >
            <span style={{ fontSize: "16px", color: "#374151" }}>Total:</span>
            <span
              style={{
                fontSize: "20px",
                fontWeight: "700",
                color: "#1f2937",
              }}
            >
              {order.currency} {order.amount}
            </span>
          </div>

          <p style={{ fontSize: "14px", color: "#6b7280", marginBottom: "20px" }}>
            Payment will be processed securely through Paystack
          </p>
        </div>

        <div style={{ display: "flex", gap: "12px" }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: "12px 16px",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              backgroundColor: "white",
              color: "#374151",
              fontSize: "14px",
              fontWeight: "500",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handlePayment}
            disabled={isLoading || isProcessing}
            className={(isLoading || isProcessing) ? "loading-button" : ""}
            style={{
              flex: 1,
              padding: "12px 16px",
              border: "none",
              borderRadius: "8px",
              backgroundColor: (isLoading || isProcessing) ? "#9ca3af" : "#10b981",
              color: "white",
              fontSize: "14px",
              fontWeight: "500",
              cursor: (isLoading || isProcessing) ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {(isLoading || isProcessing) && (
              <div className="loading-spinner" style={{
                width: "16px",
                height: "16px",
                border: "2px solid transparent",
                borderTop: "2px solid currentColor",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                marginRight: "8px"
              }} />
            )}
            {(isLoading || isProcessing) ? "Processing..." : "Pay Now"}
          </button>
        </div>
      </div>
    </div>
  )
}